import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
} from "react";

export type ThemeAppearance = "dark" | "light";

export type ThemeId =
	| "agentry-light"
	| "agentry-dark"
	| "newbiz-dark"
	| "midnight"
	| "forest"
	| "ember"
	| "ocean"
	| "sakura"
	| "lp-dark";

export type ThemeDef = {
	id: ThemeId;
	label: string;
	appearance: ThemeAppearance;
};

/** Agentry（ref/pj-agentry-1）と同じ選択肢・並び */
export const THEMES: readonly ThemeDef[] = [
	{ id: "agentry-light", label: "Agentry ライト", appearance: "light" },
	{ id: "agentry-dark", label: "Agentry ダーク", appearance: "dark" },
	{ id: "newbiz-dark", label: "NewBiz ダーク", appearance: "dark" },
	{ id: "midnight", label: "ミッドナイト", appearance: "dark" },
	{ id: "forest", label: "フォレスト", appearance: "dark" },
	{ id: "ember", label: "エンバー", appearance: "dark" },
	{ id: "ocean", label: "オーシャン", appearance: "light" },
	{ id: "sakura", label: "サクラ", appearance: "light" },
	{ id: "lp-dark", label: "LP ダーク", appearance: "dark" },
] as const;

export const DEFAULT_THEME_ID: ThemeId = "agentry-light";
export const DEFAULT_DARK_THEME_ID: ThemeId = "agentry-dark";
export const DEFAULT_NEWBIZ_THEME_ID: ThemeId = "newbiz-dark";
export const DEFAULT_LP_THEME_ID: ThemeId = "lp-dark";

const STORAGE_KEY = "agentry-theme";
const NEWBIZ_STORAGE_KEY = "agentry-theme:newbiz";
const LP_STORAGE_KEY = "agentry-theme:lp";

/** Office Visual（旧コージー/ネオン等）への統合マップ */
export function officeThemeFor(shellTheme: ThemeId): "cozy" | "neon" | "mono" | "flat" {
	switch (shellTheme) {
		case "agentry-dark":
		case "midnight":
		case "newbiz-dark":
			return "neon";
		case "forest":
		case "ocean":
			return "flat";
		case "ember":
		case "lp-dark":
			return "mono";
		default:
			return "cozy";
	}
}

/** catalog を import せずにサイトを判定（循環参照を避ける） */
function currentSiteId(): string {
	if (typeof window === "undefined") return "default";
	const host = window.location.hostname.trim().toLowerCase().replace(/\.$/, "");
	const localHosts = new Set([
		"localhost",
		"127.0.0.1",
		"::1",
		"stg-v1.agentry.jp",
		"agentry-stg-v1.fly.dev",
	]);
	if (localHosts.has(host)) {
		const requested = new URLSearchParams(window.location.search).get("site");
		if (requested === "lp" || requested === "newbiz" || requested === "creator") return requested;
	}
	if (host.startsWith("lp.") || host === "lp.agentry.jp") return "lp";
	if (host.startsWith("newbiz.") || host === "newbiz.agentry.jp") return "newbiz";
	return "default";
}

function storageKeyForSite(siteId: string): string {
	if (siteId === "newbiz") return NEWBIZ_STORAGE_KEY;
	if (siteId === "lp") return LP_STORAGE_KEY;
	return STORAGE_KEY;
}

function fallbackThemeId(siteId: string): ThemeId {
	if (siteId === "newbiz") return DEFAULT_NEWBIZ_THEME_ID;
	if (siteId === "lp") return DEFAULT_LP_THEME_ID;
	return DEFAULT_THEME_ID;
}

function isThemeId(value: string): value is ThemeId {
	return THEMES.some((theme) => theme.id === value);
}

function readStoredTheme(): ThemeId {
	const siteId = currentSiteId();
	try {
		const saved = window.localStorage.getItem(storageKeyForSite(siteId));
		if (saved && isThemeId(saved)) return saved;
		const legacy = window.localStorage.getItem("agentry:ui-theme");
		if (legacy && isThemeId(legacy)) return legacy;
	} catch {
		/* ignore */
	}
	return fallbackThemeId(siteId);
}

function applyThemeAttr(themeId: ThemeId) {
	document.documentElement.dataset["theme"] = themeId;
	document.documentElement.style.colorScheme =
		THEMES.find((theme) => theme.id === themeId)?.appearance ?? "light";
	try {
		window.localStorage.setItem(storageKeyForSite(currentSiteId()), themeId);
		window.dispatchEvent(new CustomEvent("agentry:theme", { detail: { themeId } }));
	} catch {
		/* ignore */
	}
}

type ThemeContextValue = {
	themeId: ThemeId;
	setThemeId: (id: ThemeId) => void;
	themes: readonly ThemeDef[];
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [themeId, setThemeIdState] = useState<ThemeId>(() =>
		typeof window === "undefined" ? DEFAULT_THEME_ID : readStoredTheme(),
	);

	useLayoutEffect(() => {
		applyThemeAttr(themeId);
	}, [themeId]);

	const setThemeId = useCallback((id: ThemeId) => {
		setThemeIdState(id);
	}, []);

	const value = useMemo(
		() => ({ themeId, setThemeId, themes: THEMES }),
		[themeId, setThemeId],
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
	const ctx = useContext(ThemeContext);
	if (!ctx) throw new Error("useTheme requires ThemeProvider");
	return ctx;
}
